"use client";

import { trackComponent } from "@repo/analytics";
import BFLButton from "@repo/shared-ui/BFLButton";
import { addStash } from "@repo/shared-utils/common-utils";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  dlElementConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import {
  selectIsAuthSuccessOrPerpetual,
  setIsCommonSsoModalVisible,
  store,
  useAppSelector,
} from "@repo/web-store";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import WatchlistModal from "../../../components/Watchlist/WatchlistModal";
import { handleRedirection } from "../../../utils/helper";
import type {
  TrendingStocksCardData,
  TrendingStocksFieldLabels,
  TrendingStockTradeAction,
  TrendingStocksWatchlistConfig,
} from "../types";
import TredingStockCardAccordion from "./TredingStockCardAccordion";
import TredingStockCardFooter from "./TredingStockCardFooter";
import TredingStockCardBadge from "./TredingStockCardBadge";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import { trendingStocksTrackAnalytics } from "../utils/trendingStocksCarouselAnalytics";

interface TrendingStockCardProps {
  stock: TrendingStocksCardData;
  fieldLabels?: TrendingStocksFieldLabels;
  watchlist?: TrendingStocksWatchlistConfig;
  buyText: string;
  sellText: string;
  variant?: "default" | "carousel";
  showExpandToggle?: boolean;
  /** When set, fires trackComponent for card and expand/collapse (CleverTap). */
  analyticsContext?: {
    sectionTitle: string;
    entryPoint: string;
    componentPosition?: string;
    subSection?: string;
  };
  onTradeClick: (
    action: TrendingStockTradeAction,
    stock: TrendingStocksCardData,
  ) => void | Promise<void>;
}

const TrendingStockCard: FC<TrendingStockCardProps> = ({
  stock,
  fieldLabels,
  watchlist,
  buyText,
  sellText,
  variant = "default",
  showExpandToggle = true,
  analyticsContext,
  onTradeClick,
}) => {
  const router = useRouter();
  const loginStatus = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const [isExpanded, setIsExpanded] = useState(
    Boolean(stock.isExpandedDefault),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isBookmarked = false;
  const gainSoFar = String(stock.gainSoFar ?? "");
  const percentageChange = String(stock.percentageChange ?? "");
  const valueChange = String(stock.valueChange ?? "");
  const hasExpandableDetails = Boolean(
    stock.buyPrice || stock.targetPrice || stock.stopLoss || stock.potentialReturns,
  );
  const effectiveExpanded = loginStatus && isExpanded;
  const callOptionLabel = stock.callOptionLabel?.trim();
  const dateLabel = stock.callDateLabel?.trim() || "--";
  const timeLabel = stock.callTimeLabel?.trim() || "--";

  const isNegativeGain = useMemo(() => gainSoFar.includes("-"), [gainSoFar]);
  const isNegativeChange = useMemo(
    () => percentageChange.includes("-") || valueChange.includes("-"),
    [percentageChange, valueChange],
  );

  const trendColorClass = isNegativeChange
    ? "text-[#b40000]"
    : "text-[#067161]";
  const trendArrowClass = isNegativeChange
    ? String.raw`before:content-['\e981']`
    : String.raw`before:content-['\e980']`;

  const selectedStock = useMemo(
    () => ({
      exchange: stock.exchange,
      token: stock.token,
      symbol: stock.symbol,
      fullName: stock.stockName,
    }),
    [stock.exchange, stock.stockName, stock.symbol, stock.token],
  );

  const fireExpandTrack = useCallback(() => {
    if (!analyticsContext) return;
    const willBeExpanded = !isExpanded;
    void (async () => {
      try {
        const customerId = await getCustomerIdForAnalytics();
        const trackPayload = {
          componentName: trendingStocksTrackAnalytics.componentNamePlp,
          sectionTitle: analyticsContext.sectionTitle,
          eventType: dlEventConstants.CLICK,
          clickType: trendingStocksTrackAnalytics.clickTypes.expand,
          ctaText: willBeExpanded ? "Expand details" : "Collapse details",
          productCategory: trendingStocksTrackAnalytics.productCategory,
          cleverTapOverrides: {
            EP_PROPERTY_TYPE: trendingStocksTrackAnalytics.propertyType,
            EP_PRODUCT_NAME: trendingStocksTrackAnalytics.productName,
            EP_CAMPAIGN: trendingStocksTrackAnalytics.campaign,
            EP_SPRINT: trendingStocksTrackAnalytics.sprint,
            EP_REFERENCE_ID: trendingStocksTrackAnalytics.referenceId,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            ...(analyticsContext.componentPosition && {
              EP_SR_NUMBER: analyticsContext.componentPosition,
            }),
            EP_ENTRYPOINT: analyticsContext.entryPoint,
            EP_STOCK_SYMBOL: stock.symbol,
            EP_EXPANDED: String(willBeExpanded),
          },
        };
        await trackComponent(trackPayload);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track trending stocks expand click", error);
      }
    })();
  }, [analyticsContext, isExpanded, stock.symbol]);

  const handleBookmarkClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const analyticsData = {
        section_title: watchlist?.section_title ?? "Trending stocks",
        component: watchlist?.section_title ?? "Trending stocks",
        event: dlEventConstants.CLICK,
        element: dlElementConstants.WATCH_LIST_ICON_CLICK,
        cta_text: watchlist?.cta_text ?? "Add to Watchlist",
        event_type: dlEventConstants.ICON_CLICK,
      };

      const cleverTapObj = {
        EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
        EP_TILE_CLICKED: stock.stockName ?? stock.symbol,
        EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
      };
      dataLayer("", analyticsData, cleverTapObj);

      if (!loginStatus) {
        store.dispatch(setIsCommonSsoModalVisible(true));
        return;
      }

      setIsModalOpen(true);
    },
    [
      loginStatus,
      stock.stockName,
      stock.symbol,
      watchlist?.cta_text,
      watchlist?.section_title,
    ],
  );

  const fireButtonClickTrack = useCallback(
    async (ctaText: string) => {
      if (!analyticsContext) return;

      const customerId = await getCustomerIdForAnalytics();
      await trackComponent({
        componentName: trendingStocksTrackAnalytics.componentNameCarousel,
        componentId: trendingStocksTrackAnalytics.referenceIdLinkClick,
        sectionTitle: analyticsContext.sectionTitle,
        eventType: dlEventConstants.CLICK,
        clickType: trendingStocksTrackAnalytics.clickTypes.buttonClick,
        ctaText,
        tabPosition: analyticsContext.componentPosition,
        productCategory: trendingStocksTrackAnalytics.productCategory,
        subSection: stock.symbol,
        cleverTapOverrides: {
          EP_PROPERTY_TYPE: trendingStocksTrackAnalytics.propertyType,
          EP_PRODUCT_NAME: trendingStocksTrackAnalytics.productName,
          EP_CAMPAIGN: trendingStocksTrackAnalytics.campaign,
          EP_SPRINT: trendingStocksTrackAnalytics.sprint,
          EP_REFERENCE_ID: trendingStocksTrackAnalytics.referenceIdLinkClick,
          ...(customerId && { EP_CUSTOMER_ID: customerId }),
          ...(analyticsContext.componentPosition && {
            EP_SR_NUMBER: analyticsContext.componentPosition,
          }),
          EP_ENTRYPOINT: analyticsContext.entryPoint,
          EP_SUB_SECTION: stock.symbol,
        },
      });
    },
    [analyticsContext, stock.symbol],
  );

  const handleBuyClick = useCallback(() => {
    void (async () => {
      try {
        await fireButtonClickTrack(buyText);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track trending stocks buy click", error);
      }
    })();
    const tradeAction = onTradeClick("buy", stock);
    if (tradeAction instanceof Promise) {
      tradeAction.catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Failed to handle trending stocks buy trade click", error);
      });
    }
  }, [buyText, fireButtonClickTrack, onTradeClick, stock]);

  const handleSellClick = useCallback(() => {
    void (async () => {
      try {
        await fireButtonClickTrack(sellText);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track trending stocks sell click", error);
      }
    })();
    const tradeAction = onTradeClick("sell", stock);
    if (tradeAction instanceof Promise) {
      tradeAction.catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Failed to handle trending stocks sell trade click", error);
      });
    }
  }, [fireButtonClickTrack, onTradeClick, sellText, stock]);

  const maxWidthClass =
    variant === "carousel" ? "max-w-[550px]" : "max-w-[700px]";

  const cardHref = useMemo(() => {
    if (!stock.redirectURL) return undefined;
    return addStash(stock.redirectURL);
  }, [stock.redirectURL]);

  const fireCardClickTrack = useCallback(() => {
    if (!analyticsContext || !cardHref) return;

    void (async () => {
      try {
        const customerId = await getCustomerIdForAnalytics();
        await trackComponent({
          componentName: trendingStocksTrackAnalytics.componentNameCarousel,
          componentId: trendingStocksTrackAnalytics.referenceIdLinkClick,
          sectionTitle: analyticsContext.sectionTitle,
          eventType: dlEventConstants.CLICK,
          clickType: trendingStocksTrackAnalytics.clickTypes.cardClick,
          ctaText: stock.symbol,
          destinationUrl: cardHref,
          tabPosition: analyticsContext.componentPosition,
          productCategory: trendingStocksTrackAnalytics.productCategory,
          subSection: analyticsContext.subSection,
          cleverTapOverrides: {
            EP_PROPERTY_TYPE: trendingStocksTrackAnalytics.propertyType,
            EP_PRODUCT_NAME: trendingStocksTrackAnalytics.productName,
            EP_CAMPAIGN: trendingStocksTrackAnalytics.campaign,
            EP_SPRINT: trendingStocksTrackAnalytics.sprint,
            EP_REFERENCE_ID: trendingStocksTrackAnalytics.referenceIdLinkClick,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            ...(analyticsContext.componentPosition && {
              EP_SR_NUMBER: analyticsContext.componentPosition,
            }),
            EP_ENTRYPOINT: analyticsContext.entryPoint,
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track trending stocks card click", error);
      }
    })();
  }, [analyticsContext, cardHref, stock.symbol]);

  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!cardHref || isModalOpen) return;

      const target = e.target as HTMLElement;
      if (target.closest("button")) return;

      fireCardClickTrack();
      handleRedirection(cardHref, router);
    },
    [cardHref, fireCardClickTrack, isModalOpen, router],
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- card click navigates to PDP; inner buttons handle their own actions
    <article
      aria-label={`${stock.symbol} trading call`}
      className={`w-full ${maxWidthClass} rounded-md border border-[#E5E5E5] bg-white p-[12px] md:p-[16px] text-gray-800${cardHref ? " cursor-pointer" : ""}`}
      onClick={cardHref ? handleCardClick : undefined}
    >
      <div className="flex gap-3 flex-col pb-0">
        <div className="flex select-none items-center justify-between">
          <div className="text-[12px] md:text-sm font-normal text-[#1A1A1A]">
            {fieldLabels?.gainSoFar ?? "gain so far"}{" "}
            <span
              className={`mx-1 ${isNegativeGain ? "text-[#b40000]" : "text-[#067161]"}`}
            >
              •
            </span>{" "}
            <span
              className={isNegativeGain ? "text-[#b40000]" : "text-[#067161]"}
            >
              {gainSoFar || "--"}
            </span>
          </div>

          {hasExpandableDetails && showExpandToggle && loginStatus && (
            <BFLButton
              type="button"
              aria-label={isExpanded ? "Collapse details" : "Expand details"}
              className="flex h-[12px] w-[12px] shrink-0 cursor-pointer items-center justify-center p-0 text-[#1A1A1A] transition-colors hover:text-gray-800"
              onClick={(e) => {
                e.stopPropagation();
                fireExpandTrack();
                setIsExpanded((prev) => !prev);
              }}
            >
              <svg
                className={`block h-[12px] w-[6px] shrink-0 origin-center transition-transform duration-300 ${isExpanded ? "rotate-[270deg]" : "rotate-90"}`}
                viewBox="0 0 8 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M1 1.5L7 7.5L1 13.5"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </BFLButton>
          )}
        </div>

        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-[14px] md:text-[16px] font-normal text-[#1A1A1A]">
              {stock.symbol}
            </h2>
            <div className="flex items-center gap-2">
              <TredingStockCardBadge variant="gray">
                {stock.callTypeLabel}
              </TredingStockCardBadge>
              <TredingStockCardBadge variant={stock.callStatusLabel === "OPEN" ? "teal" : "red"}>
                {stock.callStatusLabel}
              </TredingStockCardBadge>
              {callOptionLabel && (
                <TredingStockCardBadge variant={callOptionLabel == "FUTURE" ? "gold" : "red"}>
                  {callOptionLabel}
                </TredingStockCardBadge>
              )}
            </div>
          </div>

          {/* Right Side: Price & Bookmark */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-1 items-end text-right">
              <div
                className={`flex items-center text-[14px] md:text-[16px] font-normal ${trendColorClass}`}
              >
                <span>₹</span>
                <span>{stock.ltp}</span>
                <i className={`icomoon ml-1 w-3 h-3 md:w-4 md:h-4 ${trendArrowClass}`} />
              </div>
              <div className="text-[12px] md:text-[14px] text-[#666666]">
                {(valueChange || "--")} ({(percentageChange || "--")})
              </div>
            </div>
            <BFLButton
              className="text-[#4A6784] hover:text-gray-600"
              aria-label={(
                fieldLabels?.addToWatchlistAriaLabel ??
                "Add {symbol} to watchlist"
              ).replace("{symbol}", stock.symbol)}
              onClick={handleBookmarkClick}
            >
              <svg
                className="h-[16px] w-[16px] md:h-[20px] md:w-[20px]"
                fill={isBookmarked ? "#4A6784" : "none"}
                stroke="#4A6784"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                />
              </svg>
            </BFLButton>
          </div>
        </div>
      </div>

      {hasExpandableDetails && (
        <TredingStockCardAccordion
          isExpanded={effectiveExpanded}
          buyPrice={stock.buyPrice}
          targetPrice={stock.targetPrice}
          stopLoss={stock.stopLoss}
          potentialReturns={stock.potentialReturns}
          moneySymbol="₹"
          labels={fieldLabels}
        />
      )}

      <TredingStockCardFooter
        date={dateLabel}
        time={timeLabel}
        buyText={buyText}
        sellText={sellText}
        onBuyClick={handleBuyClick}
        onSellClick={handleSellClick}
      />

      {watchlist?.modalTitle && (
        <WatchlistModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedStock={selectedStock}
          successMsg={watchlist.successMsg}
          errorMsg={watchlist.errorMsg}
          modalTitle={watchlist.modalTitle}
          modalBtnCta={watchlist.modalBtnCta}
        />
      )}
    </article>
  );
};

export default TrendingStockCard;
